import React, { useEffect, useState } from "react";
import { Award, AlertTriangle, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard } from "@/components/Primitives";

export default function CMETracker() {
  const [prog, setProg] = useState(null);
  const [courses, setCourses] = useState([]);
  const [renew, setRenew] = useState([]);

  useEffect(() => {
    api.get("/cme/progress").then(r=>setProg(r.data));
    api.get("/cme/courses").then(r=>setCourses(r.data));
    api.get("/cme/renewals").then(r=>setRenew(r.data));
  }, []);

  return (
    <>
      <PageHeader eyebrow="Module 18" title="CME & Licensing Tracker"
        subtitle="Continuing medical education progress, course recommendations and license/credential renewal countdowns." testid="cme-page" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="CME Progress" subtitle={prog?`State: ${prog.state} • Due ${prog.deadline}`:""} testid="cme-progress">
          {prog && (
            <>
              <div className="text-4xl font-semibold text-brand-primary">{prog.earned}<span className="text-lg text-brand-muted">/{prog.required}</span></div>
              <div className="h-2.5 bg-brand-bg rounded-full mt-3 overflow-hidden">
                <div className="h-full bg-brand-primary" style={{width: `${prog.progress_pct}%`}}></div>
              </div>
              <div className="text-xs text-brand-muted mt-2">{prog.progress_pct}% complete</div>
              <div className="mt-4 pt-4 border-t border-brand space-y-2">
                {prog.by_category.map((c,i)=>(
                  <div key={i}>
                    <div className="flex justify-between text-xs"><span>{c.category}</span><span className="text-brand-muted">{c.earned}/{c.required}</span></div>
                    <div className="h-1.5 bg-brand-bg rounded-full mt-1 overflow-hidden">
                      <div className={`h-full ${c.earned>=c.required?"bg-emerald-500":"bg-amber-500"}`} style={{width: `${Math.min(100, (c.earned/c.required)*100)}%`}}></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </SectionCard>

        <div className="lg:col-span-2">
          <SectionCard title="Recommended Courses" subtitle="Matched to your specialty + practice patterns" testid="cme-courses">
            <div className="space-y-2">
              {courses.map((c,i)=>(
                <div key={i} className="flex items-center justify-between p-3 border border-brand rounded-md hover:bg-brand-bg">
                  <div className="flex items-start gap-3">
                    <BookOpen className="w-4 h-4 text-brand-primary mt-0.5" />
                    <div>
                      <div className="text-sm font-medium">{c.title}</div>
                      <div className="text-xs text-brand-muted">{c.provider} • {c.credits} credits</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="status-pill status-approved">{(c.match_score*100).toFixed(0)}% match</div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>

      <SectionCard title="Renewal Calendar" subtitle="Licenses, certifications, registrations" testid="cme-renewals">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {renew.map((r,i)=>(
            <div key={i} className={`p-4 border rounded-lg ${r.alert?"border-red-200 bg-red-50":"border-brand bg-white"}`}>
              <div className="flex items-start justify-between">
                <Award className={`w-4 h-4 ${r.alert?"text-red-600":"text-brand-primary"}`} />
                {r.alert && <AlertTriangle className="w-4 h-4 text-red-600" />}
              </div>
              <div className="text-sm font-semibold mt-2">{r.type}{r.state?` (${r.state})`:""}</div>
              <div className={`text-2xl font-semibold mt-1 ${r.alert?"text-red-600":"text-brand-text"}`}>{r.days_remaining}<span className="text-xs text-brand-muted ml-1">days</span></div>
            </div>
          ))}
        </div>
      </SectionCard>
    </>
  );
}
